import React, { useEffect, useState } from 'react';
import {
  Copy, Check, ExternalLink, Star, Share2, Award,
  TrendingUp, Download, ChevronDown, ChevronUp,
  CheckCircle, Clock, AlertCircle, Building2,
  History, CreditCard, Landmark
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import api from '@/utils/api';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => (n || 0).toLocaleString('pt-PT');

function MonthBar({ earnings }) {
  const n = earnings.activated_this_month || 0;
  const target = earnings.bonus_target || 10;
  const pct = Math.min(100, Math.round((n / target) * 100));
  return (
    <div className="mb-4" data-testid="month-bonus-bar">
      <div className="flex justify-between text-xs text-gray-500 mb-1.5">
        <span className="font-semibold text-gray-700">{n} activadas este mês</span>
        <span>{earnings.bonus_this_month > 0 ? 'Bónus do mês conquistado!' : `Bónus de 2.000 ECV aos ${target}`}</span>
      </div>
      <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, background: pct >= 100 ? 'linear-gradient(90deg,#059669,#10b981)' : 'linear-gradient(90deg,#d97706,#f59e0b)' }} />
      </div>
    </div>
  );
}

const Stat = ({ label, value, tone, testid }) => (
  <div className={`rounded-xl p-3 border text-center ${tone}`} data-testid={testid}>
    <div className="text-lg font-black leading-tight">{fmt(value)}</div>
    <div className="text-[10px] text-gray-500 mt-0.5">{label}</div>
  </div>
);

function EarningsCard({ earnings }) {
  return (
    <div className="mb-4 space-y-2">
      <div className="grid grid-cols-3 gap-2">
        <Stat label="ECV empresas activadas" value={earnings.fixed} tone="bg-white border-amber-100 text-amber-600" testid="amb-fixed-earnings" />
        <Stat label="ECV comissões" value={earnings.commissions} tone="bg-white border-blue-100 text-blue-600" testid="amb-commission-earnings" />
        <Stat label="ECV bónus do mês" value={earnings.bonus_this_month} tone="bg-white border-indigo-100 text-indigo-600" testid="amb-bonus-earnings" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Stat label="ECV por pagar" value={earnings.pending} tone="bg-emerald-50 border-emerald-200 text-emerald-700" testid="amb-pending-earnings" />
        <Stat label="ECV já pagos" value={earnings.total_paid} tone="bg-gray-50 border-gray-100 text-gray-700" testid="amb-paid-earnings" />
      </div>
    </div>
  );
}

function CompanyRow({ c }) {
  return (
    <div className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-bold
        ${c.status === 'activada' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-50 text-amber-600'}`}>
        {(c.company_name || c.email || '?').charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-800 truncate">{c.company_name || '—'}</div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold rounded-full px-1.5 py-0.5
            ${c.has_nif ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'}`}>
            NIF {c.has_nif ? '✓' : '—'}
          </span>
          <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold rounded-full px-1.5 py-0.5
            ${c.has_job ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'}`}>
            Vaga {c.has_job ? '✓' : '—'}
          </span>
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <div className={`flex items-center justify-end gap-1 text-xs font-bold
          ${c.status === 'activada' ? 'text-emerald-600' : 'text-amber-500'}`}>
          {c.status === 'activada'
            ? <><CheckCircle className="h-3.5 w-3.5" /> Activada</>
            : <><Clock className="h-3.5 w-3.5" /> Pendente</>}
        </div>
        {(c.fixed > 0 || c.commission_total > 0) && (
          <div className="text-[10px] text-gray-500 mt-0.5" data-testid="amb-company-earnings">
            {c.fixed > 0 && `${fmt(c.fixed)} fixo`}{c.fixed > 0 && c.commission_total > 0 && ' · '}
            {c.commission_total > 0 && `${fmt(c.commission_total)} comissões (${c.commissions.length})`}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AmbassadorPanel() {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [copied, setCopied]     = useState('');
  const [downloading, setDownloading] = useState(false);
  const [showCompanies, setShowCompanies] = useState(false);
  const [showHistory, setShowHistory]     = useState(false);

  useEffect(() => {
    api.get('/candidate/ambassador')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const copy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key); toast.success('Copiado!');
      setTimeout(() => setCopied(''), 2000);
    });
  };

  const downloadCertificate = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('trabadju_access_token');
      const resp  = await fetch(`${API_URL}/api/candidate/ambassador/certificate`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = 'Certificado_Embaixador_Trabadju.pdf'; a.click();
      URL.revokeObjectURL(url);
      toast.success('Certificado descarregado!');
    } catch {
      toast.error('Erro ao descarregar o certificado.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading || !data) return null;

  // Candidato ainda não foi seleccionado pelo admin
  if (!data.joined) {
    return null; // O painel não aparece — candidato precisa de se candidatar à vaga e ser seleccionado
  }

  const { stats, earnings, companies = [], code, link, payment_history = [] } = data;

  return (
    <Card className="p-5 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50" data-testid="ambassador-panel">

      {/* Header */}
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center flex-shrink-0">
          <Star className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="font-bold text-gray-900">Programa Embaixador Trabadju</h2>
          <p className="text-xs text-gray-500 mt-0.5">600 ECV por empresa activada · 30% nos primeiros 3 meses · bónus mensal</p>
        </div>
        <Button variant="outline" size="sm" onClick={downloadCertificate} disabled={downloading}
          className="border-amber-300 text-amber-700 hover:bg-amber-100 gap-1.5 flex-shrink-0 text-xs"
          data-testid="download-certificate-btn">
          <Award className="h-3.5 w-3.5" />
          {downloading ? 'A gerar…' : 'Certificado'}
        </Button>
      </div>

      {/* Tabela de ganhos */}
      <div className="bg-white border border-amber-100 rounded-xl px-3 py-2 mb-4 text-xs text-gray-600 space-y-1" data-testid="amb-rules">
        <div><strong className="text-amber-700">600 ECV</strong> por cada empresa activada</div>
        <div><strong className="text-blue-700">30%</strong> do que a empresa pagar nos primeiros 3 meses (mínimo 500 ECV no 1.º pagamento)</div>
        <div><strong className="text-indigo-700">2.000 ECV</strong> de bónus a cada mês com 10 empresas activadas</div>
      </div>

      <MonthBar earnings={earnings || {}} />

      {/* Ganhos */}
      <EarningsCard earnings={earnings || {}} />

      {/* Condições */}
      <div className="bg-white border border-gray-100 rounded-xl px-3 py-2 mb-4">
        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Condições de activação</p>
        <div className="flex gap-3 text-xs text-gray-600">
          <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3 text-emerald-500" /> NIF da empresa</span>
          <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3 text-emerald-500" /> 1.ª vaga publicada</span>
        </div>
      </div>

      {/* Código */}
      <div className="mb-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">O teu código exclusivo</p>
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-white border-2 border-amber-300 rounded-xl px-4 py-3 font-mono font-bold text-xl text-amber-700 tracking-widest text-center"
            data-testid="ambassador-code">{code}</div>
          <button onClick={() => copy(code, 'code')} className="p-3 bg-amber-100 hover:bg-amber-200 rounded-xl transition-colors">
            {copied === 'code' ? <Check className="h-5 w-5 text-emerald-600" /> : <Copy className="h-5 w-5 text-amber-700" />}
          </button>
        </div>
      </div>

      {/* Link */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Link de registo partilhável</p>
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-white border border-gray-200 rounded-xl px-3 py-2 text-xs text-blue-600 truncate font-medium"
            data-testid="ambassador-link">{link}</div>
          <button onClick={() => copy(link, 'link')} className="p-2.5 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors flex-shrink-0">
            {copied === 'link' ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4 text-blue-600" />}
          </button>
        </div>
      </div>

      {/* Acções */}
      <div className="flex gap-2 mb-4">
        <Button variant="outline" size="sm" className="flex-1 border-amber-300 text-amber-700 hover:bg-amber-100"
          onClick={() => copy(link, 'share')} data-testid="amb-copy-link-btn">
          <Share2 className="h-4 w-4 mr-1.5" /> Partilhar link
        </Button>
        <Button variant="outline" size="sm" className="border-gray-200 text-gray-600 hover:bg-gray-50"
          onClick={() => window.open('/jobs/search?q=embaixador+trabadju', '_blank')}
          data-testid="amb-view-job-btn" title="Ver vaga de Embaixador">
          <ExternalLink className="h-4 w-4" />
        </Button>
      </div>

      {/* Lista de empresas */}
      {companies.length > 0 && (
        <div>
          <button
            onClick={() => setShowCompanies(v => !v)}
            className="w-full flex items-center justify-between text-xs font-semibold text-gray-500 uppercase tracking-wider py-2 hover:text-gray-700 transition-colors"
            data-testid="toggle-companies-btn">
            <span>
              Empresas ({stats.active_companies} activadas · {stats.pending_companies} pendentes)
            </span>
            {showCompanies ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
          {showCompanies && (
            <div className="bg-white rounded-xl border border-gray-100 p-3 mt-1" data-testid="companies-list">
              {companies.map((c, i) => <CompanyRow key={i} c={c} />)}
            </div>
          )}
        </div>
      )}

      {/* Histórico de pagamentos */}
      {payment_history.length > 0 && (
        <div className="mt-3">
          <button
            onClick={() => setShowHistory(v => !v)}
            className="w-full flex items-center justify-between text-xs font-semibold text-gray-500 uppercase tracking-wider py-2 hover:text-gray-700 transition-colors"
            data-testid="toggle-history-btn">
            <span className="flex items-center gap-1.5">
              <History className="h-3.5 w-3.5" /> Histórico de Pagamentos ({payment_history.length})
            </span>
            {showHistory ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
          {showHistory && (
            <div className="bg-white rounded-xl border border-emerald-100 p-3 mt-1 space-y-3" data-testid="payment-history">
              {payment_history.map((p, i) => (
                <div key={i} className="flex items-start gap-3 pb-3 border-b border-gray-50 last:border-0">
                  <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    {p.method === 'vinti4' ? <CreditCard className="h-4 w-4 text-emerald-600" /> : <Landmark className="h-4 w-4 text-emerald-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-emerald-700">
                        {p.amount?.toLocaleString('pt-PT')} ECV
                      </span>
                      <span className="text-xs text-gray-400">
                        {p.authorized_at ? new Date(p.authorized_at).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 capitalize">
                      {p.method === 'vinti4' ? 'Vinti4' : 'Transferência'} · {p.active_companies_at_payment} empresas activadas
                    </div>
                    {p.note && <div className="text-xs text-gray-400 mt-0.5 italic">{p.note}</div>}
                  </div>
                  <span className="flex-shrink-0 bg-emerald-100 text-emerald-700 text-[10px] font-bold px-2 py-0.5 rounded-full">
                    Pago
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {companies.length === 0 && (
        <p className="text-xs text-gray-400 text-center mt-2">
          Partilha o teu link — as empresas registadas aparecem aqui
        </p>
      )}
    </Card>
  );
}
